import type { FilterTerm } from "./tags";
import { Device } from "../store/slices/deviceSlice";
import { apiClient } from "./api";

export const fetchDevices = async (): Promise<Device[]> => {
  const response = await apiClient.get<Device[]>("/devices");
  return response.data;
};

export const fetchDeviceDetails = async (deviceId: string): Promise<Device> => {
  const response = await apiClient.get<Device>(`/devices/${deviceId}/details`);

  // Use only the new monitoredTopics format
  const monitoredTopics = response.data.monitoredTopics || [];

  const processedDevice = {
    ...response.data,
    monitoredTopics,
    // Backend returns discoveredSources already in ArboristNodeData tree form
    discoveredSources: response.data.discoveredSources || [],
  };

  return processedDevice;
};

/** A device deleted from the UI whose agent still calls the API. */
export interface DeletedDevice {
  deviceId: string;
  name?: string;
  deletedAt: string;
  lastContactAt?: string;
  lastContactIp?: string;
}

/** Deleted devices whose agent called in the last week: robots still running
 *  an agent that should be uninstalled or paired again. */
export const fetchContactingDeletedDevices = async (): Promise<
  DeletedDevice[]
> => {
  const response = await apiClient.get<DeletedDevice[]>("/devices/deleted");
  return response.data;
};

/** Hides a deleted device from the notice until its agent calls again. */
export const dismissDeletedDevice = async (deviceId: string): Promise<void> => {
  await apiClient.post(`/devices/deleted/${deviceId}/dismiss`);
};

export const fetchDeviceStatus = async () => {
  const response =
    await apiClient.get<Array<{ id: string; online: boolean }>>(
      "/devices/status",
    );
  return response.data;
};

/** A time range that has been uploaded (nanoseconds since epoch).
 *  s3_keys is populated once the segment has been stitched to S3 — the key is the pipeline name. */
export interface UploadedTimeRange {
  start: number; // nanoseconds, rounded by JSON.parse: display only
  end: number; // nanoseconds, rounded by JSON.parse: display only
  // The same bounds as exact decimal strings. Nanosecond timestamps are above
  // 2^53, so only these address the segment on view, download and delete.
  start_ns?: string;
  end_ns?: string;
  s3_keys?: Record<string, string>;
}

/** Exact segment bounds to send back to the API. Older APIs send numbers only. */
export const segmentBoundsNs = (
  seg: UploadedTimeRange,
): { start: string; end: string } => ({
  start: seg.start_ns ?? String(seg.start),
  end: seg.end_ns ?? String(seg.end),
});

export interface DeviceRecord {
  recording_id: string;
  start_time: string; // ISO string
  end_time: string; // ISO string
  size_bytes: number;
  status: "AVAILABLE" | "EVICTED_LOCALLY";
  cloud_status:
    | "NOT_UPLOADED"
    | "QUEUED"
    | "UPLOADING"
    | "PARTIAL"
    | "FULL"
    | "STITCHING"
    | "READY";
  uploaded_ranges: UploadedTimeRange[];
  pipelines: string[];
}

export interface DeviceRecordingsResponse {
  records: DeviceRecord[];
  total: number;
  page: number;
  page_size: number;
}

export const fetchDeviceRecordings = async (
  deviceId: string,
  page = 1,
  pageSize = 10,
): Promise<DeviceRecordingsResponse> => {
  const response = await apiClient.get<DeviceRecordingsResponse>(
    `/devices/${deviceId}/recordings`,
    { params: { page, page_size: pageSize } },
  );
  return response.data;
};

/** One device's recording coverage over a window, bucketed oldest to newest.
 *  Each bucket is the fraction of that slice of time covered by a recording. */
export interface DeviceRecordingCoverage {
  device_id: string;
  buckets: number[];
  covered_seconds: number;
  last_recording_at?: string | null;
}

export interface RecordingCoverageResponse {
  start: string;
  end: string;
  buckets: number;
  coverage: DeviceRecordingCoverage[];
}

/**
 * Fleet-wide recording coverage in one request. The device list draws a
 * sparkline per row, so this deliberately replaces what would otherwise be one
 * timeline call per visible device.
 */
export const fetchDevicesRecordingCoverage = async (
  hours = 24,
  buckets = 32,
): Promise<RecordingCoverageResponse> => {
  const response = await apiClient.get<RecordingCoverageResponse>(
    "/devices/recordings-coverage",
    { params: { hours, buckets } },
  );
  return response.data;
};

export interface TimelineBlock {
  recording_id: string;
  start_time: string; // ISO string
  end_time: string; // ISO string
  pipelines: string[];
  size_bytes: number;
  status: "AVAILABLE" | "EVICTED_LOCALLY";
  cloud_status: "NOT_UPLOADED" | "PARTIAL" | "FULL" | "STITCHING" | "READY";
  job_status?: string; // status of the recording's open upload job, if any
  job_progress?: number; // Progress percentage if job is active
  uploaded_ranges: UploadedTimeRange[]; // nanoseconds since epoch; s3_keys populated once stitched
  /** Windows of the recording's open uploads in this segment. */
  pending_ranges?: PendingUploadRange[];
}

/** The window of an open upload; bounds are nanoseconds as decimal strings. */
export interface PendingUploadRange {
  start_ns: string;
  end_ns: string;
  status: string;
  progress: number;
  priority?: "urgent" | "normal";
}

export const fetchDeviceRecordingsTimeline = async (
  deviceId: string,
  start?: string,
  end?: string,
  /** Keeps recordings that started while the device's tags matched. */
  where?: FilterTerm[],
): Promise<TimelineBlock[]> => {
  const params: any = {};
  if (start) params.start = start;
  if (end) params.end = end;
  if (where?.length) params.where = JSON.stringify(where);

  const response = await apiClient.get<TimelineBlock[]>(
    `/devices/${deviceId}/recordings/timeline`,
    { params },
  );
  return response.data;
};

export const fetchDeviceRecordingsList = async (
  deviceId: string,
  start?: string,
  end?: string,
): Promise<DeviceRecord[]> => {
  const params: any = {};
  if (start) params.start = start;
  if (end) params.end = end;

  const response = await apiClient.get<DeviceRecord[]>(
    `/devices/${deviceId}/recordings/list`,
    { params },
  );
  return response.data;
};

export interface Task {
  recording_id: string;
  start_time: string;
  end_time: string;
  pipelines: string[];
}

export const requestRecordingUpload = async (
  deviceId: string,
  tasks: Task[],
  options: { urgent?: boolean } = {},
): Promise<void> => {
  await apiClient.post(
    `/devices/${deviceId}/recordings/upload-selection`,
    tasks,
    options.urgent ? { params: { priority: "urgent" } } : undefined,
  );
};
export interface UploadJob {
  upload_id: string;
  batch_id: string;
  device_id: string;
  device_name?: string;
  device_online?: boolean;
  recording_id: string;
  status: string;
  /** "urgent" goes before the device's other uploads. */
  priority?: "urgent" | "normal";
  progress_percent: number;
  req_start_time: string;
  req_end_time: string;
  req_pipelines?: string[];
  reason?: string; // why it FAILED
  code?: string; // reason code, e.g. EVICTED_LOCALLY
  created_at: string;
  updated_at: string;
}

/** The org's open recording uploads and those that failed in the last 24 h. */
export const fetchUploadJobs = async (): Promise<UploadJob[]> =>
  (await apiClient.get<UploadJob[]>("/devices/uploads")).data;

/** Cancels an open upload together with the rest of its batch. */
export const cancelUploadJob = async (deviceId: string, jobId: string) =>
  apiClient.post(`/devices/${deviceId}/recordings/uploads/${jobId}/cancel`);

export const getRecordingViewerURL = async (
  deviceId: string,
  recordingId: string,
  pipeline: string,
  segmentStartNs?: string,
  segmentEndNs?: string,
): Promise<{ url: string }> => {
  const params: Record<string, string | number> = { pipeline };
  if (segmentStartNs) params.segment_start = segmentStartNs;
  if (segmentEndNs) params.segment_end = segmentEndNs;
  const response = await apiClient.get<{ url: string }>(
    `/devices/${deviceId}/recordings/${recordingId}/viewer-url`,
    { params },
  );
  return response.data;
};

export const getRecordingDownloadURL = async (
  deviceId: string,
  recordingId: string,
  pipeline: string,
  segmentStartNs?: string,
  segmentEndNs?: string,
): Promise<{ url: string; filename: string }> => {
  const params: Record<string, string | number> = { pipeline };
  if (segmentStartNs) params.segment_start = segmentStartNs;
  if (segmentEndNs) params.segment_end = segmentEndNs;
  const response = await apiClient.get<{ url: string; filename: string }>(
    `/devices/${deviceId}/recordings/${recordingId}/s3-download`,
    { params },
  );
  return response.data;
};

export interface Incident {
  id: string;
  alarm_id: string;
  alarm_name: string;
  status: string;
  triggered_at: string;
  resolved_at?: string;
  details: any;
}

export const fetchDeviceIncidents = async (
  deviceId: string,
): Promise<Incident[]> => {
  const response = await apiClient.get<Incident[]>(
    `/devices/${deviceId}/incidents`,
  );
  return response.data;
};
